import { useState, useEffect, useRef } from 'preact/hooks';
import { api } from '../api/client';
import { T } from '../styles/tokens';
import { Button, Textarea } from './ui';

interface StarRatingProps {
  /** The task/run ID to rate */
  taskId: string;
  /** Read-only mode (just display average) */
  readOnly?: boolean;
  /** Size in px (default 24) */
  size?: number;
  /** Show rating count tooltip */
  showCount?: boolean;
  /** Callback after rating saved */
  onRated?: (rating: number) => void;
}

interface RatingData {
  rating: number;
  comment: string | null;
}

const STAR_COLORS = {
  filled: T.warning,    // amber-400
  half: T.warning,
  empty: T.bgSecondary,     // gray-700
  hover: T.warning,     // amber-500
};

export function StarRating({ taskId, readOnly = false, size = 24, showCount = false, onRated }: StarRatingProps) {
  const [currentRating, setCurrentRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [saving, setSaving] = useState(false);
  const [showComment, setShowComment] = useState(false);
  const [comment, setComment] = useState('');
  const [justSaved, setJustSaved] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const commentRef = useRef<HTMLTextAreaElement>(null);

  // Fetch existing rating
  useEffect(() => {
    if (!taskId) return;
    api.get<{ rating: RatingData | null }>(`/cloud/tasks/${taskId}/rating`)
      .then((res: any) => {
        if (res?.rating) {
          setCurrentRating(res.rating);
          if (res.comment) setComment(res.comment);
        } else if (typeof res === 'object' && res !== null && 'rating' in res && res.rating !== null) {
          setCurrentRating(res.rating);
          if (res.comment) setComment(res.comment);
        }
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, [taskId]);

  const submitRating = async (rating: number, commentText?: string) => {
    setSaving(true);
    try {
      await api.post(`/cloud/tasks/${taskId}/rate`, {
        rating,
        ...(commentText ? { comment: commentText } : {}),
      });
      setCurrentRating(rating);
      setJustSaved(true);
      setShowComment(false);
      onRated?.(rating);
      setTimeout(() => setJustSaved(false), 1500);
    } catch (e) {
      console.error('Failed to save rating:', e);
    }
    setSaving(false);
  };

  const handleStarClick = (star: number) => {
    if (readOnly || saving) return;
    if (star === currentRating) {
      // Same star → toggle comment box
      setShowComment(!showComment);
      return;
    }
    submitRating(star, comment || undefined);
  };

  const handleCommentSubmit = () => {
    if (!comment.trim() && currentRating === 0) return;
    submitRating(currentRating || hoverRating || 5, comment.trim() || undefined);
  };

  const displayRating = hoverRating || currentRating;

  if (!loaded) return null;

  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column', gap: 4 }}>
      {/* Stars row */}
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 2,
        cursor: readOnly ? 'default' : 'pointer',
        opacity: saving ? 0.5 : 1,
        transition: 'opacity 0.2s',
      }}>
        {[1, 2, 3, 4, 5].map(star => {
          const isFilled = star <= displayRating;
          const isHovered = !readOnly && hoverRating > 0 && star <= hoverRating;
          return (
            <span
              key={star}
              onClick={(e) => { e.stopPropagation(); handleStarClick(star); }}
              onMouseEnter={() => !readOnly && setHoverRating(star)}
              onMouseLeave={() => !readOnly && setHoverRating(0)}
              style={{
                fontSize: size,
                lineHeight: 1,
                color: isFilled ? (isHovered ? STAR_COLORS.hover : STAR_COLORS.filled) : STAR_COLORS.empty,
                transform: justSaved && star <= currentRating ? 'scale(1.3)' : isHovered ? 'scale(1.15)' : 'scale(1)',
                transition: 'all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)',
                userSelect: 'none',
                filter: isFilled ? `drop-shadow(0 0 3px ${T.warningBgStrong})` : 'none',
              }}
              title={readOnly ? `${star} estrela${star > 1 ? 's' : ''}` : `Avaliar ${star} estrela${star > 1 ? 's' : ''}`}
            >
 
            </span>
          );
        })}

        {/* Rating number */}
        {currentRating > 0 && (
          <span style={{
            fontSize: size * 0.5,
            color: T.textSecondary,
            marginLeft: 6,
            fontWeight: 600,
            fontVariantNumeric: 'tabular-nums',
          }}>
            {currentRating}/5
          </span>
        )}

        {/* Saved confirmation */}
        {justSaved && (
          <span style={{
            fontSize: size * 0.45,
            color: T.success,
            marginLeft: 6,
            fontWeight: 600,
            animation: 'star-fade-in 0.3s ease-out',
          }}>
            Salvo
          </span>
        )}
      </div>

      {/* Comment popup */}
      {showComment && !readOnly && (
        <div style={{
          marginTop: 8,
          background: T.bg,
          border: '1px solid #2a2a3e',
          borderRadius: 10,
          padding: 12,
          animation: 'star-slide-down 0.2s ease-out',
          maxWidth: 320,
        }}>
          <Textarea
            ref={commentRef}
            value={comment}
            onInput={(e) => setComment((e.target as HTMLTextAreaElement).value)}
            placeholder="Comentário opcional..."
            style={{
              width: '100%',
              minHeight: 60,
              background: T.bg,
              border: '1px solid #21262d',
              borderRadius: 8,
              color: T.textSecondary,
              fontSize: 13,
              padding: 10,
              resize: 'vertical',
              outline: 'none',
              fontFamily: 'inherit',
            }}
            onFocus={() => commentRef.current?.select()}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
            <Button variant="ghost"
              onClick={(e) => { e.stopPropagation(); setShowComment(false); }}
              style={{
                padding: '6px 14px', borderRadius: 8, fontSize: 12,
                background: 'transparent', border: '1px solid #374151',
                color: T.textSecondary, cursor: 'pointer',
              }}
            >
              Cancelar
            </Button>
            <Button variant="ghost"
              onClick={(e) => { e.stopPropagation(); handleCommentSubmit(); }}
              disabled={saving}
              style={{
                padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                background: T.warning, border: 'none',
                color: '#111', cursor: saving ? 'not-allowed' : 'pointer',
              }}
            >
              {saving ? '...' : 'Salvar'}
            </Button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes star-fade-in {
          from { opacity: 0; transform: translateX(-4px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes star-slide-down {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
